import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useSearchParams } from 'react-router'
import { usePostHog } from '@posthog/react'
import { ScrollShadow } from '@heroui/react'
import { useQuery } from '@tanstack/react-query'
import { motion, useAnimate } from 'framer-motion'
import {
  type ProductrecommenderpbReturnItem,
  type PbshoppingProductInfo,
  ProductrecommenderpbGetRecommendationsRequestChannelEnum,
  ProductrecommenderpbRecommendedProduct
} from '@itsrever/rever-api'
import { useTranslate } from '@/hooks'
import { productRecommendationsQuery } from '@/hooks/use-product-recommendations'
import { useAppSelector } from '@/redux/hooks'
import { calculateAmountDue } from '@/app/full-catalog/helpers/calculate-amount-due'
import { MediaImageIcon } from '@/ui/icons/media-image'
import { FlowPrimaryButton, FlowSecondaryButton } from './flow-buttons'

const DEFAULT_PRODUCTS_SHOWN = 6

interface ItemProductRecommendationsProps {
  onClose: () => void
  refundAmount: number | null
  returnedItem: ProductrecommenderpbReturnItem
}

export const ItemProductRecommendations = ({
  onClose,
  refundAmount,
  returnedItem
}: ItemProductRecommendationsProps) => {
  const {
    i18n: { language }
  } = useTranslation()
  const { translate } = useTranslate()

  const [showProducts, setShowProducts] = useState(false)

  const [scope, animate] = useAnimate()

  const posthog = usePostHog()

  const [searchParams] = useSearchParams()

  const { order, checkout_id } = useAppSelector(
    (store) => store.returnsApi.order.response
  )

  const ecommerceId = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_id
  )

  const orderReference = order?.identification?.customer_printed_order_id

  const { data, status } = useQuery({
    ...productRecommendationsQuery({
      channel: ProductrecommenderpbGetRecommendationsRequestChannelEnum.Portal,
      checkout_id,
      ecommerce_id: ecommerceId,
      order_reference: orderReference,
      return_items: [
        {
          comment: returnedItem.comment ?? '',
          product_id: returnedItem.product_id ?? '',
          refund_amount: returnedItem.refund_amount,
          return_reason: returnedItem.return_reason,
          variant_id: returnedItem.variant_id
        }
      ],
      user_information: {
        address: order?.shipping_address?.address_line_1,
        country_code: order?.shipping_address?.country_code ?? '',
        currency_code: order?.money_format?.currency ?? '',
        email: order?.customer?.email ?? '',
        language
      }
    }),
    retry: false
  })

  const createQueryString = (
    product?: ProductrecommenderpbRecommendedProduct
  ) => {
    const params = new URLSearchParams(searchParams.toString())

    params.set('catalog', 'true')

    if (product?.product_id) {
      params.set('from_product_id', product.product_id)
    }

    return params.toString()
  }

  const onFullCatalogClick = (product?: PbshoppingProductInfo) => {
    posthog.capture('Exchange FullCatalog Clicked', {
      element_type: product ? 'product_recommendation' : 'button_test',
      product: product,
      checkout_id
    })

    onClose()
  }

  const isRefunding = refundAmount !== null

  useEffect(() => {
    animate([
      [
        '#selection-title',
        { opacity: 1, filter: 'blur(0px)' },
        { duration: 0.7 }
      ],
      [
        '#products-title',
        { opacity: 1, filter: 'blur(0px)' },
        { duration: 0.6 }
      ]
    ]).then(() => setShowProducts(true))
  }, [animate])

  useEffect(() => {
    if (
      status === 'success' &&
      data.recommended_products &&
      data.recommended_products?.length > 0
    ) {
      posthog.capture('Product Recommendations Viewed', {
        checkout_id,
        source: refundAmount !== null ? 'Exchange' : 'Refund',
        candidate_generation_option_chosen:
          data.candidate_generation_option_chosen,
        copywriting_option_chosen: data.copywriting_option_chosen,
        diversification_option_chosen: data.diversification_option_chosen,
        filtering_option_chosen: data.filtering_option_chosen,
        recommendations_uuid: data.recommendations_uuid,
        recommended_products: data.recommended_products,
        scoring_option_chosen: data.scoring_option_chosen
      })
    }
  }, [status, data, posthog, checkout_id, refundAmount])

  return (
    <div className="flex flex-1 flex-col overflow-hidden" ref={scope}>
      <ScrollShadow className="flex-1 overflow-y-auto" size={80}>
        <div className="border-b border-solid border-neutral-200 p-4 md:p-5">
          <motion.div
            id="selection-title"
            initial={{ opacity: 0, filter: 'blur(2px)' }}
            className="flex flex-col items-center gap-3.5"
          >
            {isRefunding ? (
              refundAmount > 0 ? (
                <>
                  <div className="rounded-full bg-neutral-100 p-1.5">
                    <CreditCardIcon />
                  </div>
                  <p className="text-center text-base">
                    <Trans
                      i18nKey="initial_selection.full_catalog_refund_label"
                      values={{
                        refundAmount: refundAmount.toLocaleString(language, {
                          style: 'currency',
                          currency: order?.money_format?.currency
                        })
                      }}
                      components={{
                        strong: <strong className="font-semibold" />
                      }}
                    />
                  </p>
                </>
              ) : (
                <>
                  <div className="rounded-full bg-neutral-100 p-1.5">
                    <CheckIcon />
                  </div>
                  <p
                    className="text-center text-base"
                    dangerouslySetInnerHTML={{
                      __html: translate(
                        'item_selection_full_catalog_return_selected'
                      )
                    }}
                  />
                </>
              )
            ) : (
              <>
                <div className="rounded-full bg-neutral-100 p-1.5">
                  <CheckIcon />
                </div>
                <p
                  className="text-center text-base"
                  dangerouslySetInnerHTML={{
                    __html: translate(
                      'item_selection_full_catalog_exchange_selected'
                    )
                  }}
                />
              </>
            )}
          </motion.div>
        </div>
        <div className="flex-1 space-y-5 p-4 md:p-5">
          <motion.p
            id="products-title"
            initial={{ opacity: 0, filter: 'blur(1.5px)' }}
            className="text-center text-[15px]"
            dangerouslySetInnerHTML={{
              __html: translate(
                isRefunding
                  ? 'item_selection_full_catalog_refund_label'
                  : 'item_selection_full_catalog_exchange_label'
              )
            }}
          />
          {status === 'success' && showProducts && (
            <motion.ul
              className="grid gap-x-1.5 gap-y-4 [grid-template-columns:repeat(auto-fill,minmax(110px,1fr))] sm:grid-cols-3"
              variants={{
                show: {
                  transition: {
                    delayChildren: 0.2,
                    staggerChildren: 0.2
                  }
                }
              }}
              initial="hidden"
              animate="show"
            >
              {data.recommended_products
                ?.slice(0, DEFAULT_PRODUCTS_SHOWN)
                .map((product) => {
                  const parsedProductPrice = product.price_range?.min?.amount
                    ? parseFloat(product.price_range?.min?.amount)
                    : null

                  const amountDue =
                    parsedProductPrice && isRefunding
                      ? calculateAmountDue(parsedProductPrice, refundAmount)
                      : 0

                  return (
                    <motion.li
                      key={product.product_id}
                      variants={{
                        hidden: {
                          opacity: 0,
                          filter: 'blur(4px)',
                          y: 20
                        },
                        show: {
                          filter: 'blur(0px)',
                          opacity: 1,
                          y: 0,
                          transition: {
                            duration: 0.5,
                            ease: 'easeOut'
                          }
                        }
                      }}
                    >
                      <Link
                        to={{
                          search: '?' + createQueryString(product)
                        }}
                        className="group block h-40 w-full overflow-hidden rounded-md"
                        onClick={() => onFullCatalogClick(product)}
                      >
                        {product.image_url ? (
                          <img
                            height={160}
                            width={150}
                            src={product.image_url}
                            alt={product.name}
                            className="aspect-[4/5] size-full object-cover transition-transform group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
                            <MediaImageIcon width={20} height={20} />
                          </div>
                        )}
                      </Link>
                      <div className="pt-1">
                        <p className="truncate text-[11px] uppercase">
                          {product.name}
                        </p>
                        {isRefunding ? (
                          <div className="flex items-center gap-2 pt-0.5">
                            <div
                              style={{
                                boxShadow: '0px 0px 5.7px 0px #FFF inset'
                              }}
                              className="w-fit rounded-full bg-radial-gradient from-brand-800 to-brand-800 px-2.5 py-0.5 shadow shadow-brand-300"
                            >
                              <p className="text-[0.8125rem] text-white drop-shadow-sm">
                                {amountDue.toLocaleString(language, {
                                  style: 'currency',
                                  currency: product.price_range?.min?.currency
                                })}
                              </p>
                            </div>
                            {parsedProductPrice ? (
                              <span className="text-xs text-neutral-500 line-through">
                                {parsedProductPrice.toLocaleString(language, {
                                  style: 'currency',
                                  currency: product.price_range?.min?.currency
                                })}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    </motion.li>
                  )
                })}
            </motion.ul>
          )}
        </div>
      </ScrollShadow>
      <motion.div
        initial={{ opacity: 0, filter: 'blur(1.5px)' }}
        animate={{ opacity: 1, filter: 'blur(0px)' }}
        transition={{ duration: 0.5, delay: 2 }}
        className="mt-auto flex flex-col gap-2 p-4 md:p-5"
      >
        <FlowPrimaryButton
          as={Link}
          to={{
            search: '?' + createQueryString()
          }}
          onPress={() => onFullCatalogClick()}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: translate(
                isRefunding
                  ? refundAmount > 0
                    ? 'item_selection_full_catalog_refund_primary_action_button'
                    : 'item_selection_refund_zero_primary_action_button'
                  : 'item_selection_full_catalog_exchange_primary_action_button'
              )
            }}
          />
        </FlowPrimaryButton>
        <FlowSecondaryButton onPress={onClose}>
          <span
            dangerouslySetInnerHTML={{
              __html: translate(
                'item_selection_full_catalog_secondary_action_button'
              )
            }}
          />
        </FlowSecondaryButton>
      </motion.div>
    </div>
  )
}

const CheckIcon = () => {
  return (
    <svg
      width="15"
      height="15"
      strokeWidth="2"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color="#000000"
    >
      <motion.path
        variants={{
          normal: {
            pathLength: 1,
            transition: {
              duration: 0.3
            }
          },
          animate: {
            pathLength: [0, 1],
            transition: {
              delay: 0.25,
              duration: 0.6
            }
          }
        }}
        initial="normal"
        animate="animate"
        d="M5 13L9 17L19 7"
        stroke="#000000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

const CreditCardIcon = () => {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      strokeWidth="2"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      color="#000000"
    >
      <motion.path
        d="M22 9V17C22 18.1046 21.1046 19 20 19H4C2.89543 19 2 18.1046 2 17V7C2 5.89543 2.89543 5 4 5H20C21.1046 5 22 5.89543 22 7V9ZM22 9H6"
        stroke="#000000"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }}
      />
    </svg>
  )
}
